import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames/bind';

import Heading from 'components/shared/heading';
import useAutoChangeableIndex from 'hooks/use-auto-changeable-index';

import ProgressCircle from './progress-circle';

import styles from './tab-features-with-animation.module.scss';

import shape1 from './svg/shape-1.svg';
import shape2 from './svg/shape-2.svg';
import dotPattern from './svg/dot-pattern.svg';

import SecurityIcon from './svg/security.inline.svg';
import FaceRecognitionIcon from './svg/face-recognition.inline.svg';
import AnalyticsIcon from './svg/analytics.inline.svg';
import CheckShieldIcon from './svg/check-shield.inline.svg';
import HouseIcon from './svg/house.inline.svg';
import CogIcon from './svg/cog.inline.svg';

const cx = classNames.bind(styles);

const ITEM_CHANGE_INTERVAL = 5000; // milliseconds

const iconsCollection = {
  security: SecurityIcon,
  faceRecognition: FaceRecognitionIcon,
  analytics: AnalyticsIcon,
  checkShield: CheckShieldIcon,
  house: HouseIcon,
  cog: CogIcon,
};

const featureCardAnimationVariants = {
  initial: {
    opacity: 0,
  },
  active: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
  },
};

const TabFeaturesWithAnimation = ({ items }) => {
  const [animationStartRef, isAnimationStarted] = useInView({
    triggerOnce: true,
  });

  const [activeItemIndex, startAnimation, restartAnimation] = useAutoChangeableIndex(items.length, {
    interval: ITEM_CHANGE_INTERVAL,
  });

  useEffect(() => {
    if (isAnimationStarted) startAnimation();
  }, [startAnimation, isAnimationStarted]);

  return (
    <section className={cx('wrapper')}>
      <div className="container" ref={animationStartRef}>
        <div className="columns">
          <motion.div className={cx('column is-12-mobile is-3-tablet is-5-desktop', 'left')}>
            {items.map(({ title, iconName }, tabIndex) => {
              const Icon = iconsCollection[iconName];
              const isActive = isAnimationStarted && tabIndex === activeItemIndex;

              return (
                <div
                  className={cx('tab', { 'tab-active': isActive })}
                  role="button"
                  tabIndex="0"
                  onClick={() => restartAnimation(tabIndex)}
                  onKeyDown={() => restartAnimation(tabIndex)}
                  key={tabIndex}
                >
                  <Heading
                    className={cx('tab-title')}
                    tag="h3"
                    size="md"
                    color="tertiary"
                    innerHTML={title}
                  />
                  <ProgressCircle className={cx('tab-progress-circle')} animate={isActive}>
                    <Icon className={cx('tab-icon')} />
                  </ProgressCircle>
                </div>
              );
            })}
          </motion.div>

          <div
            className={cx(
              'column is-12-mobile is-10-tablet is-6-desktop is-5-widescreen is-offset-0-mobile is-offset-1-desktop',
              'right'
            )}
          >
            <AnimatePresence exitBeforeEnter>
              {items.map(({ title, text }, index) =>
                index === activeItemIndex ? (
                  <motion.div
                    variants={featureCardAnimationVariants}
                    animate="active"
                    initial="initial"
                    exit="exit"
                    key={index}
                  >
                    <Heading
                      className={cx('title')}
                      tag="h2"
                      size="xl"
                      color="tertiary"
                      innerHTML={title}
                    />
                    <div className={cx('text')} dangerouslySetInnerHTML={{ __html: text }} />
                  </motion.div>
                ) : null
              )}
            </AnimatePresence>

            <img className={cx('dot-pattern')} src={dotPattern} loading="lazy" aria-hidden alt="" />
          </div>
        </div>

        <img className={cx('shape-1')} src={shape1} loading="lazy" aria-hidden alt="" />
        <img className={cx('shape-2')} src={shape2} loading="lazy" aria-hidden alt="" />
      </div>
    </section>
  );
};

TabFeaturesWithAnimation.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      iconName: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default TabFeaturesWithAnimation;
